// Per-user todo list. Tenants see meters whose reading is due (or close to due)
// plus their unpaid proformas; landlords see PENDING readings awaiting approval
// plus open proformas they've issued. Both lists come from a single GET /tasks
// call so a user holding both roles gets a combined view.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon, unitIcon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { formatDate, formatDateTime, formatMoney } from '../core/format.js';

export class TasksView extends Component {
  constructor() {
    super();
    this.state = { data: null, loadError: null };
  }

  template() {
    if (this.state.loadError) {
      return `<section class="stack"><h2>${esc(t('tasks.title'))}</h2>
        <div class="error-banner">${esc(this.state.loadError)}</div></section>`;
    }
    if (!this.state.data) {
      return `<section class="stack"><h2>${esc(t('tasks.title'))}</h2>
        <div class="muted">${esc(t('tasks.loading'))}</div></section>`;
    }
    const tenant = this.state.data.tenant_tasks || [];
    const owner = this.state.data.owner_tasks || [];
    const tenantPay = this.state.data.tenant_payables || [];
    const ownerRecv = this.state.data.owner_receivables || [];
    if (tenant.length === 0 && owner.length === 0 && tenantPay.length === 0 && ownerRecv.length === 0) {
      return `<section class="stack"><h2>${esc(t('tasks.title'))}</h2>
        <div class="card empty">${esc(t('tasks.empty'))}</div></section>`;
    }
    return `
      <section class="stack">
        <h2>${esc(t('tasks.title'))}</h2>
        ${tenantPay.length ? this._tenantPayablesSection(tenantPay) : ''}
        ${ownerRecv.length ? this._ownerReceivablesSection(ownerRecv) : ''}
        ${tenant.length ? this._tenantSection(tenant) : ''}
        ${owner.length ? this._ownerSection(owner) : ''}
      </section>
    `;
  }

  _tenantSection(items) {
    return `
      <div class="card stack">
        <div class="stack-2">
          <h3>${esc(t('tasks.tenant.section'))}</h3>
          <span class="faint">${esc(t('tasks.tenant.section_help'))}</span>
        </div>
        <div class="list">${items.map((it) => this._tenantRow(it)).join('')}</div>
      </div>
    `;
  }

  _tenantRow(it) {
    const badge = this._dueBadge(it);
    const label = it.meter_label || it.meter_kind;
    const where = `${this._unitLabel(it)} · ${esc(it.property_name || '')}`;
    const href = `#/tenancies/${encodeURIComponent(it.tenancy_id)}?tab=meters`;
    return `
      <div class="list-item">
        ${icon(unitIcon(it.unit_type), { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(label)}</strong>
          <span class="faint">${where}</span>
          <span class="faint">${esc(t('tasks.tenant.next_due', { date: formatDate(it.next_due_date) }))}</span>
        </div>
        ${badge}
        <a class="btn btn-ghost" href="${href}">${esc(t('tasks.tenant.open'))}</a>
      </div>
    `;
  }

  _dueBadge(it) {
    const d = Number(it.days_until_due);
    if (it.status === 'overdue' || d < 0) {
      const n = Math.abs(d);
      const key = n === 1 ? 'tasks.tenant.overdue_one' : 'tasks.tenant.overdue_other';
      return `<span class="badge badge-warning">${esc(t(key, { count: n }))}</span>`;
    }
    if (it.status === 'due_today' || d === 0) {
      return `<span class="badge badge-warning">${esc(t('tasks.tenant.due_today'))}</span>`;
    }
    const key = d === 1 ? 'tasks.tenant.due_in_days_one' : 'tasks.tenant.due_in_days_other';
    const klass = it.status === 'upcoming' ? 'badge badge-muted' : 'badge';
    return `<span class="${klass}">${esc(t(key, { count: d }))}</span>`;
  }

  _ownerSection(items) {
    return `
      <div class="card stack">
        <div class="stack-2">
          <h3>${esc(t('tasks.owner.section'))}</h3>
          <span class="faint">${esc(t('tasks.owner.section_help'))}</span>
        </div>
        <div class="list">${items.map((it) => this._ownerRow(it)).join('')}</div>
      </div>
    `;
  }

  _ownerRow(it) {
    const label = it.meter_label || it.meter_kind;
    const where = `${this._unitLabel(it)} · ${esc(it.property_name || '')}`;
    const value = t('tasks.owner.value', {
      value: it.reading_value,
      unit: it.unit_of_measure || '',
    }).trim();
    const submitted = it.submitted_at
      ? esc(t('tasks.owner.submitted_at', { date: formatDateTime(it.submitted_at) }))
      : '';
    const submittedBy = (it.tenant_name || it.tenant_email)
      ? esc(t('tasks.owner.submitted_by', { name: it.tenant_name || it.tenant_email }))
      : '';
    return `
      <div class="list-item">
        ${icon(unitIcon(it.unit_type), { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(label)} — ${esc(value)}</strong>
          <span class="faint">${where}</span>
          <span class="faint">${submitted}${submittedBy ? ' ' + submittedBy : ''}</span>
        </div>
        <span class="badge badge-warning">PENDING</span>
        <a class="btn btn-ghost" href="#/properties/${encodeURIComponent(it.property_id)}">${esc(t('tasks.owner.open'))}</a>
      </div>
    `;
  }

  _tenantPayablesSection(items) {
    return `
      <div class="card stack">
        <div class="stack-2">
          <h3>${esc(t('tasks.tenant_payables.section'))}</h3>
          <span class="faint">${esc(t('tasks.tenant_payables.section_help'))}</span>
        </div>
        <div class="list">${items.map((it) => this._payableRow(it, true)).join('')}</div>
      </div>
    `;
  }

  _ownerReceivablesSection(items) {
    return `
      <div class="card stack">
        <div class="stack-2">
          <h3>${esc(t('tasks.owner_receivables.section'))}</h3>
          <span class="faint">${esc(t('tasks.owner_receivables.section_help'))}</span>
        </div>
        <div class="list">${items.map((it) => this._payableRow(it, false)).join('')}</div>
      </div>
    `;
  }

  _payableRow(it, isTenant) {
    const where = it.property_names
      ? esc(it.property_names) + (it.unit_identifiers ? ' · ' + esc(it.unit_identifiers) : '')
      : '';
    const counterKey = isTenant ? 'tasks.tenant_payables.counterparty' : 'tasks.owner_receivables.counterparty';
    const counter = it.counterparty_name
      ? esc(t(counterKey, { name: it.counterparty_name }))
      : '';
    const open = formatMoney(it.open_amount, it.currency);
    const total = formatMoney(it.total_amount, it.currency);
    const amountLine = it.paid_amount > 0
      ? esc(t('tasks.payable.partial', { open, total }))
      : esc(t('tasks.payable.amount', { open }));
    const href = `#/tenancies/${encodeURIComponent(it.tenancy_id)}?tab=billing`;
    const openKey = isTenant ? 'tasks.tenant_payables.open' : 'tasks.owner_receivables.open';
    return `
      <div class="list-item">
        ${icon('receipt', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(it.document_number)} — ${amountLine}</strong>
          <span class="faint">${where}</span>
          <span class="faint">${counter}</span>
        </div>
        ${this._payableBadge(it)}
        <a class="btn btn-ghost" href="${href}">${esc(t(openKey))}</a>
      </div>
    `;
  }

  _payableBadge(it) {
    if (it.due_date === null || it.due_date === undefined) {
      return `<span class="badge">${esc(t('tasks.payable.no_due_date'))}</span>`;
    }
    const d = Number(it.days_until_due);
    if (it.is_overdue || d < 0) {
      const n = Math.abs(d);
      const key = n === 1 ? 'tasks.payable.overdue_one' : 'tasks.payable.overdue_other';
      return `<span class="badge badge-warning">${esc(t(key, { count: n }))}</span>`;
    }
    if (d === 0) return `<span class="badge badge-warning">${esc(t('tasks.payable.due_today'))}</span>`;
    const key = d === 1 ? 'tasks.payable.due_in_days_one' : 'tasks.payable.due_in_days_other';
    return `<span class="badge">${esc(t(key, { count: d }))}</span>`;
  }

  _unitLabel(it) {
    const id = it.unit_identifier ? ` · ${esc(it.unit_identifier)}` : '';
    return `${esc(it.unit_type || '')}${id}`;
  }

  mount() {
    if (this.state.data === null && !this.state.loadError) this._load();
  }

  async _load() {
    try {
      const data = await api.get('/tasks');
      this.setState({ data });
    } catch (err) {
      this.setState({ loadError: err instanceof ApiError ? err.message : t('tasks.errors.load') });
    }
  }
}
